import React from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Loader, ProfilePicture } from '@getro/rombo';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import { ShowMoreContacts } from 'components/atoms/showMoreContacts';
import { TableRow } from '../agGrid/components/tableRow';
import ContactActionMenu from './contactActionMenu';
import { ContactEmailDropdown } from '../contactEmailDropdown';

export const ContactNameRenderer = ({ data, ...props }) => {
  const history = useHistory();
  if (!data?.original) {
    return '';
  }
  const { contact, companyId } = data.original;
  const { network, queryParams, sharedList } = data;

  if (data.original.id === 'load-more') {
    return (
      <ShowMoreContacts
        contactsCount={data.original.contactsCount}
        currentContactsCount={data.original.currentContactsCount}
        contactsSource={data.contactsSource}
        networkId={data.network.id}
        sharedListId={data.sharedList.id}
        companyId={data.original.companyId}
        queryParams={data.queryParams}
        isPublicList={data.isPublicList}
      />
    );
  }

  if (!contact?.id) {
    return (
      <TableRow {...props} width="100%">
        <Flex sx={{ alignItems: 'center', gap: '8px', p: '8px' }}>
          {data.isActiveSync && (
            <Box width="16px" height="16px">
              <Loader size="small" />
            </Box>
          )}
          <Text sx={{ fontSize: '14px', color: 'neutral.500', fontStyle: 'italic' }}>
            {data.isActiveSync ? 'No contacts found for now...' : 'No contacts at this company'}
          </Text>
        </Flex>
      </TableRow>
    );
  }

  return (
    <TableRow {...props} width="100%">
      <Flex
        alignItems="flex-start"
        p="8px"
        width="100%"
        sx={{
          position: 'relative',
          gap: '8px',
          '&:hover .table-item-contact-actions': {
            opacity: '1',
          },
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Flex
          onClick={() => {
            if (data.isPublicList) {
              return;
            }
            history.push(
              `/networks/${network.slug}/list/${sharedList.id}/contacts/detail/${contact.id}${history.location.search}`,
            );
          }}
          sx={{
            gap: '8px',
            cursor: data.isPublicList ? 'default' : 'pointer',
            alignItems: 'center',
            flexGrow: 1,
            textDecoration: 'none',
          }}
        >
          <Box width="24px" height="24px" flex="0 0 auto">
            <ProfilePicture imageUrl={contact.profilePicUrl} name={contact.fullName} />
          </Box>
          <Text
            fontSize="14px"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {contact.fullName}
          </Text>
        </Flex>
        <Flex
          className="table-item-contact-actions"
          alignItems="center"
          flex="0 0 auto"
          sx={{
            position: 'absolute',
            top: '0px',
            bottom: '0px',
            right: '0px',
            bg: 'neutral.20',
            gap: '8px',
            pl: '8px',
            opacity: '0',
            transition: 'opacity 0.2s ease',
          }}
        >
          {contact.linkedinUrl && (
            <Box
              as={Linkedin}
              height="16px"
              width="16px"
              aria-hidden="true"
              sx={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                window.open(contact.linkedinUrl, '_blank');
              }}
            />
          )}

          <ContactEmailDropdown contact={contact} showTooltip={false} />

          {!data.isPublicList && (
            <ContactActionMenu
              contact={contact}
              network={network}
              queryParams={queryParams}
              sharedList={sharedList}
              companyId={companyId}
            />
          )}
        </Flex>
      </Flex>
    </TableRow>
  );
};

ContactNameRenderer.propTypes = {
  data: PropTypes.object,
  context: PropTypes.object,
};

ContactNameRenderer.defaultProps = {
  data: {},
  context: {},
};
